/** @jsx jsx */
import * as React from "react";
import { jsx } from "@emotion/core";
import { connect } from "react-redux";
import ReactPaginate from 'react-paginate';

import { RouteUtils, FITNESS_PLAN_CREATE } from "../../../../routes";
import { IAppState } from "../../../../models/IAppState";
import { UserFitnessActions } from "../../../../actions/UserFitnessActions";
import { IAllFitnessPlansProps, IMapStateToProps, IMapDispatchToProps, IPaginationParams } from "../../../FitnessPlan/AllFitnessPlans/interface";
import { get } from "lodash";
import * as styles from './style';
import CTA, { CTATheme } from "../../../../components/common/CallToAction";
import { SearchBar } from "../../../../components/common/SearchBarV1/SearchBar";
import { IPaginatedMicrocycles, formFieldNames, IMicrocycle } from "../../../FitnessPlan/FitnessTemplate/interface";
import { FITNESS_GOAL, GENDER } from "../../../FitnessPlan/constants";
import { IUserFitnessPlanRequestParams } from "../../../Patient/Transform/interface";
import { FitnessTemplateStateType } from "../FitnessTemplateWrapper/FitnessTemplateWrapper";
import Paginator from "../../../../components/common/Paginator/Paginator";
import CustomSelect from "../../../../components/CustomSelect";
import { OngoingConsultationActions } from "../../../../actions/OngoingConsultationActions";

interface IOwnProps {
    setTemplateId: (planId: number) => void;
    setFitnessPlanState: (state: FitnessTemplateStateType) => void;
    onBackPress: () => void;
}

const OngoingAllFitnessPlan: React.FC<IAllFitnessPlansProps & IOwnProps> = (props) => {
    const { fetchAllExercises: fetchExercises, params, allExercises, fetchAllFitnessPlans, accountData, assignFitnessPlan, setTemplateId, setFitnessPlanState, onBackPress } = props;

    const isFirstRender = React.useRef(true)
    const PAGE_LIMIT = 10;
    const INITIAL_PAGINATION_PARAM: IPaginationParams = { offset: 0, limit: PAGE_LIMIT };
    const loggedInUserParam = { createdBy: { value: accountData.id, query: "eq" } };
    const [allFitnessPlans, setAllPlans] = React.useState<IPaginatedMicrocycles>(null);
    const [searchParams, setSearchParams] = React.useState(loggedInUserParam);
    const [paginationParams, setPaginationParams] = React.useState(INITIAL_PAGINATION_PARAM);
    const [selectedPlan, setSelectedPlan] = React.useState<IMicrocycle>(null);
    const [currentPage, setCurrentPage] = React.useState(0);
    const [isSearching, setIsSearching] = React.useState<boolean>(false);

    React.useEffect(() => {
        if (!isFirstRender.current) {
            fetchPlans();
        }
    }, [paginationParams, searchParams]);

    React.useEffect(() => {
        isFirstRender.current = false // toggle flag after first render/mounting
        //this is done so that page loads quickly while creating a template form
        if (!allExercises) {
            fetchExercises();
        }
        fetchPlans();
    }, []);

    const fetchPlans = () => {
        //add sort params
        setIsSearching(true);
        let params = { ...paginationParams, sortBy: "createdOn", sortOrder: "DESC" };
        fetchAllFitnessPlans(searchParams, params).then((plans: IPaginatedMicrocycles) => {
            setAllPlans(plans);
            setIsSearching(false);
        })
    }

    const createFitnessPlan = () => {
        setFitnessPlanState(FitnessTemplateStateType.CREATE_PLAN);
    }

    const filterPlans = (value: string, fieldName: string) => {
        value ? setSearchParams({ ...loggedInUserParam, [fieldName]: { value, query: "like" } }) : setSearchParams(loggedInUserParam);
        setPaginationParams(INITIAL_PAGINATION_PARAM)
    }

    const prevPageHandler = () => {
        if (!currentPage) return;
        setCurrentPage(currentPage => {
            const offset = (currentPage - 1) * PAGE_LIMIT;
            setPaginationParams({ offset, limit: PAGE_LIMIT });
            return currentPage - 1;
        });
    }

    const nextPageHandler = () => {
        if (currentPage === Math.ceil(allFitnessPlans.totalElements / PAGE_LIMIT)) return;
        setCurrentPage(currentPage => {
            const offset = (currentPage + 1) * PAGE_LIMIT;
            setPaginationParams({ offset, limit: PAGE_LIMIT });
            return currentPage + 1;
        });

    }

    // const duplicatePlan = (selectedPlan: IMicrocycle) => {
    //     const reqParams: IUserFitnessPlanRequestParams = {
    //         createdBy: accountData.id,
    //         description: `${selectedPlan.description}-duplicate`
    //     }
    //     assignFitnessPlan(reqParams, selectedPlan.id).then((response: any) => {
    //         fetchPlans();
    //     })
    // }

    const viewPlan = () => {
        if (!selectedPlan) return;
        setTemplateId(selectedPlan.id);
    }

    return (
        <div css={styles.outerWrapper}>
            <div css={styles.header}>
                <div css={styles.cardTitle}>
                    My Saved Plans
                </div>
                <div css={styles.filterSearchWrapper}>
                    <div css={styles.filterWrapper}>
                        <span>Filters:</span>
                        <div css={styles.customSelectWrapper}>
                            <CustomSelect dropdownValues={[]} selectedValue={""} defaultValue="All Goals" updateDropdownValue={null} keyName={formFieldNames.GOALID} disabled />
                        </div>
                        <div css={styles.customSelectWrapper}>
                            <CustomSelect dropdownValues={[]} selectedValue={""} defaultValue="All Levels" updateDropdownValue={null} keyName={formFieldNames.GOALID} disabled />
                        </div>
                    </div>
                    <SearchBar placeholder="Search by Plan Name" fieldName={formFieldNames.TEMPLATENAME} handleSubmit={(value, fieldName) => {
                        filterPlans(value, fieldName);
                        console.log("Searched clicked", value, fieldName);

                    }} />
                </div>
            </div>
            <div css={styles.plansList}>
                <table css={styles.tableWrapper}>
                    <thead>
                        <tr css={styles.tableHeader}>
                            <th css={styles.smallerCol}></th>
                            <th css={styles.bigCol}>Plan Name</th>
                            <th css={styles.smallCol}>Goal</th>
                            <th css={styles.smallCol}>Gender</th>
                            <th css={styles.smallCol}>Level</th>
                            <th css={styles.smallCol}>Workout Days</th>
                        </tr>
                    </thead>
                    {
                        (allFitnessPlans && allFitnessPlans.elements.length > 0) && <tbody>
                            {allFitnessPlans.elements.map(plan => {
                                const goalFocus = FITNESS_GOAL.find(({ internalValue }) => internalValue === plan.goalId);
                                const gender = GENDER.find(({ internalValue }) => internalValue === plan.meta.gender);

                                return (
                                    <tr css={styles.tableData} key={plan.id} onClick={() => setSelectedPlan(plan)}>
                                        <td css={[styles.smallerCol, styles.dataRow, { textAlign: 'center', padding: 0 }]}>{selectedPlan && selectedPlan.id === plan.id && <img src="/images/pink-checkbox.png" height="20px" />}</td>
                                        <td css={[styles.bigCol, styles.dataRow]}>{plan.description}</td>
                                        <td css={[styles.smallCol, styles.dataRow]}>{goalFocus ? goalFocus.displayValue : "-"}</td>
                                        <td css={[styles.smallCol, styles.dataRow]}>{gender ? gender.displayValue : "-"}</td>
                                        <td css={[styles.smallCol, styles.dataRow, styles.capitalize]}>{plan.level.toLowerCase() || '-'}</td>
                                        <td css={[styles.smallCol, styles.dataRow]}>{plan.workoutDays || '-'}</td>
                                    </tr>
                                )
                            })
                            }

                        </tbody>
                    }
                </table>
                {!isSearching && (!allFitnessPlans || allFitnessPlans.elements.length === 0) && <div css={styles.notFound}>No Plan Found!</div>}
                {isSearching && <div css={styles.searchingWrapper}><img src="/images/loader/rolling-loader.gif" /></div>}
            </div>
            {allFitnessPlans && <Paginator totalItemCount={allFitnessPlans.totalElements} onMoveBackward={prevPageHandler} onMoveForward={nextPageHandler} itemsPerPage={PAGE_LIMIT} />}
            <div css={styles.footer}>
                <div css={{ display: 'flex', cursor: 'pointer' }} onClick={createFitnessPlan}>
                    <img src="/images/add.svg" />
                    <CTA innerText="Create New Template" theme={CTATheme.TEXTUAL} onClick={null} customStyle={styles.createPlanCta} />
                </div>
                <div css={styles.ctaWrapper}>
                    <CTA innerText="Cancel" theme={CTATheme.SECONDARY} onClick={onBackPress} customStyle={styles.actionButton} />
                    <CTA innerText="Select" theme={CTATheme.PRIMARY} onClick={viewPlan} customStyle={styles.actionButton} />
                </div>
            </div>
        </div >
    )
}

const mapStateToProps = (state: IAppState, ownProps: IOwnProps): IMapStateToProps & IOwnProps => ({
    allExercises: get(state, "ongoingConsultation.fitnessPlan.exercises", null),
    accountData: state.accountData,
    ...ownProps
});

const mapDispatchToProps = (dispatch: React.Dispatch<any>): IMapDispatchToProps => ({
    fetchAllExercises: () => OngoingConsultationActions.fetchAllExercises(dispatch),
    fetchAllFitnessPlans: (params: { [key: string]: { value: string | number, query: string } }, paginationParams: IPaginationParams) => UserFitnessActions.fetchAllFitnessPlans(params, paginationParams),
    assignFitnessPlan: (requestParams: IUserFitnessPlanRequestParams, selectedPlanId: number) => UserFitnessActions.assignFitnessPlan(requestParams, selectedPlanId)
})

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(OngoingAllFitnessPlan);